"use strict";

import NodeBase from "../util/NodeBase";
import { drawRoundRect } from "../util/shapes";

/**
 * A DataUnit Node/Cluster shape.
 *
 * @augments DataUnit
 */
class DataUnit extends NodeBase {
  /**
   * @param {object} options
   * @param {object} body
   * @param {Label} labelModule
   * @param captionModule
   * @param notationModule
   * @param iconsList
   */
  constructor(options, body, labelModule, captionModule, notationModule, iconsList) {
    super(options, body, labelModule);
    this._setMargins(labelModule);
    this.iconsList = iconsList;
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} [selected]
   * @param {boolean} [hover]
   */
  resize(ctx, selected = this.selected, hover = this.hover) {
    if (this.needsRefresh(selected, hover)) {
      this.getDimensionsFromLabel(ctx, selected, hover);

      this.width = 44; //dimensions.width + this.margin.right + this.margin.left;
      this.height = 40; //dimensions.height + this.margin.top + this.margin.bottom;
      this.radius = 0; //this.width / 2;
    }
  }

  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} x width
   * @param {number} y height
   * @param {boolean} selected
   * @param {boolean} hover
   * @param {ArrowOptions} values
   * @param pointer
   */
  draw(ctx, x, y, selected, hover, values, pointer) {
    this.resize(ctx, selected, hover);
    this.left = x - this.width / 2;
    this.top = y - this.height / 2;

    this.initContextForDraw(ctx, values);
    drawRoundRect(
      ctx,
      this.left,
      this.top,
      this.width,
      this.height,
      values.borderRadius
    );
    this.performFill(ctx, values);

    this.updateBoundingBox(x, y, ctx, selected, hover);
    this.labelModule.draw(
      ctx,
      this.left + this.textSize.width / 2 + this.margin.left,
      this.top + this.textSize.height / 2 + this.margin.top,
      selected,
      hover
    );

    if (values.useToolBar) {
      this.drawToolBar(ctx, pointer, selected, hover);
    }
  }

  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param pointer
   * @param {boolean} selected
   * @param {boolean} hover
   */
  drawToolBar(ctx, pointer, selected, hover) {
    if (!selected) {
      return;
    }
    const lh = this.left + 2 + (this.options.subType === "user" ? 0 : 6);
    const th = this.top + this.height - 10 - 2;
    const atItem = this.hitActionItem(pointer);
    if (atItem === "setting" && hover) {
      ctx.drawImage(this.iconsList.setting.hover, lh, th, 10, 10);
    } else {
      ctx.drawImage(this.iconsList.setting.normal, lh, th, 10, 10);
    }
    if (atItem === "link" && hover) {
      ctx.drawImage(this.iconsList.link.hover, lh + 10 + 2, th, 10, 10);
    } else {
      ctx.drawImage(this.iconsList.link.normal, lh + 10 + 2, th, 10, 10);
    }
    if (this.options.subType === "user") {
      if (atItem === "remove" && hover) {
        ctx.drawImage(
          this.iconsList.remove.hover,
          lh + 10 + 2 + 10 + 2,
          th,
          10,
          10
        );
      } else {
        ctx.drawImage(
          this.iconsList.remove.normal,
          lh + 10 + 2 + 10 + 2,
          th,
          10,
          10
        );
      }
    }
  }

  /**
   *
   * @param {number} x width
   * @param {number} y height
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} selected
   * @param {boolean} hover
   */
  updateBoundingBox(x, y, ctx, selected, hover) {
    this._updateBoundingBox(x, y, ctx, selected, hover);

    const borderRadius = this.options.shapeProperties.borderRadius; // only effective for box
    this._addBoundingBoxMargin(borderRadius);
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} angle
   * @returns {number}
   */
  distanceToBorder(ctx, angle) {
    if (ctx) {
      this.resize(ctx);
    }
    const borderWidth = this.options.borderWidth;

    return (
      Math.min(
        Math.abs(this.width / 2 / Math.cos(angle)),
        Math.abs(this.height / 2 / Math.sin(angle))
      ) + borderWidth
    );
  }

  /**
   * @param {x: number, y: number} pointer
   */
  hitActionItem(pointer) {
    const lh = this.left + 1 + (this.options.subType === "user" ? 0 : 6);
    const rh = this.left + 2 + 10 + 2 + 10 + 2 + 10 + 1;
    const th = this.top + this.height - 10 - 4;
    const bh = this.top + this.height - 1;
    if (pointer.y > bh || pointer.y < th || pointer.x < lh || pointer.x > rh) {
      return "";
    }
    if (pointer.x < lh + 10 + 1) {
      return "setting";
    }
    if (pointer.x < lh + 10 + 2 + 10 + 1) {
      return "link";
    }
    if (this.options.subType === "user") {
      return "remove";
    }
    return "";
  }

  /**
   * @param {x: number, y: number} pointer
   */
  overActionItemName(pointer) {
    return this.hitActionItem(pointer);
  }
}

export default DataUnit;
